import {colors} from "@c10h/colors";
// @ts-expect-error TS7016: Could not find a declaration file for module 'color'. '/Users/goksel/Sites/carbon-website-next/node_modules/color/index.js' implicitly has an 'any' type.
import Color from "color";
import Image from "next/image";
import {useTranslation} from "ni18n";
import React, {useEffect, useMemo, useState} from "react";
import {usePrefersReducedMotion} from "src/hooks/usePrefersReducedMotion";
import {usePricesByRegion} from "src/hooks/usePrices";

import {SpecialtyId} from "../../../constants/specialtyIds";
import {useGetSoonestInPracticeBySpecialtyId} from "../../../hooks/useGetSoonestTime";
import {useTypedSelector} from "../../../store";
import {centsToDollarString} from "../../../utils/formatNumbers";
import {getSchedulingRedirectUrl} from "../../../utils/getRedirectUrl";
import {vcData} from "../../_common/_constants";
import {todayTomorrow2, trackToPatient, useInterval} from "../../_common/Carbon";
import styles from "./Services.module.scss";
import {useIsMobile} from "src/utils/isMobile";

const time = 20000;
const timeSec = `${time / 1000}s`;

const Services = () => {
  const i18n = useTranslation();
  const {pageLoaded} = useTypedSelector(state => state.config);
  const isMobile = useIsMobile();
  const selectedPrices = usePricesByRegion();
  const shouldReduceMotion = usePrefersReducedMotion();

  const virtualUrgentCost = useMemo(
    () => selectedPrices?.find(item => item.slug === "virtual-visit")?.price,
    [selectedPrices],
  );

  const virtualTherapyCost = useMemo(
    () => selectedPrices?.find(item => item.slug === "mental-health-visit")?.price,
    [selectedPrices],
  );

  const firstVirtualUrgentSlot = useGetSoonestInPracticeBySpecialtyId({
    specialtyId: SpecialtyId.VIRTUAL_URGENT_CARE,
  });

  const firstVirtualMentalHealthSlot = useGetSoonestInPracticeBySpecialtyId({
    specialtyId: SpecialtyId.VIRTUAL_MENTAL_HEALTH,
  });

  const slides = useMemo(
    () => [
      {
        specialtyId: SpecialtyId.VIRTUAL_URGENT_CARE,
        head: i18n.t("Everyday Care"),
        text: i18n.t(
          "From preventive to urgent care and everything in between, our virtual care providers are there for you.",
        ),
        color: vcData[SpecialtyId.VIRTUAL_URGENT_CARE].darker2,
        btnColor: "#FFF",
        btnText: i18n.t("Book appointment"),
        img: "vcServices0",
        price: virtualUrgentCost ? centsToDollarString(virtualUrgentCost) : undefined,
        firstSlot: firstVirtualUrgentSlot?.time,
      },
      {
        specialtyId: SpecialtyId.VIRTUAL_MENTAL_HEALTH,
        head: i18n.t("Mental Health"),
        text: i18n.t(
          "Need to talk things out? Book a virtual session easily through the Carbon Health app. Find a time that works for you and speak to a therapist on your time.",
        ),
        color: vcData[SpecialtyId.VIRTUAL_MENTAL_HEALTH].darker2,
        btnColor: "#FFF",
        btnText: i18n.t("Book appointment"),
        img: "vcServices2",
        price: virtualTherapyCost ? centsToDollarString(virtualTherapyCost) : undefined,
        firstSlot: firstVirtualMentalHealthSlot?.time,
      },
      {
        head: i18n.t("Nutritionist "),
        text: i18n.t(
          "We'll work with you to design a nutritional plan that checks off all your boxes.",
        ),
        color: colors.darkMint,
        btnColor: colors.gray800,
        img: "vcServices3",
        comingSoon: true,
      },
    ],
    [
      firstVirtualMentalHealthSlot?.time,
      firstVirtualUrgentSlot?.time,
      i18n,
      virtualTherapyCost,
      virtualUrgentCost,
    ],
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [delay, setDelay] = useState(time);
  const [pause, setPause] = useState(true);

  const startTime = Date.now();

  useInterval(
    () => {
      if (!isMobile) {
        setActiveIndex((activeIndex + 1) % slides.length);
      }
    },
    delay,
    pause,
  );

  const redirectToSpecialty = (specialtyId: string) => {
    trackToPatient();
    window.open(
      getSchedulingRedirectUrl({
        virtual: true,
        specialtyId,
      }),
      "_blank",
      "noopener,noreferrer",
    );
  };

  useEffect(() => {
    setDelay(time);
  }, [activeIndex]);

  return (
    <div className={`grid grid-flow-col bg-smoke ${styles.wrap}`} id="servicesWrap">
      <div className="text-center mobileContainer">
        <div className={styles.leftInner}>
          <h2>{i18n.t("Virtual Care, 365 days a year")}</h2>

          <div
            className={styles.rows}
            onMouseEnter={() => {
              const timeLeft = delay - (Date.now() - startTime);
              setDelay(timeLeft);
              setPause(true);
            }}
            onMouseLeave={() => {
              setPause(false || shouldReduceMotion);
            }}
          >
            {slides.map(
              (
                {
                  specialtyId,
                  head,
                  text,
                  color,
                  btnColor,
                  btnText,
                  img,
                  price,
                  comingSoon,
                  firstSlot,
                },
                i,
              ) => {
                const isActive = i === activeIndex;
                return (
                  <div key={head} className={`${styles.row} ${isActive ? "active" : ""}`}>
                    <Image
                      src={`/static/img/v5/${img}.jpg`}
                      height={300}
                      width={348}
                      alt=""
                      className="object-cover lg:hidden"
                    />
                    {pageLoaded && (
                      <div className={styles.rowSpinner} aria-hidden>
                        <div className="spinerContainer">
                          <div
                            className="pie spinner"
                            style={{
                              "--bg": color,
                              "--time": timeSec,
                              "--state": pause ? "paused" : "running",
                            }}
                          />
                          <div
                            className="pie filler"
                            style={{
                              "--bg": color,
                              "--time": timeSec,
                              "--state": pause ? "paused" : "running",
                            }}
                          />
                          <div
                            className="mask"
                            style={{"--time": timeSec, "--state": pause ? "paused" : "running"}}
                          />
                        </div>
                      </div>
                    )}

                    <button
                      className={`flex items-center bg-inherit p-0 brdn cursor-pointer ${styles.rowsTW}`}
                      onClick={() => setActiveIndex(i)}
                      aria-expanded={isActive}
                      aria-controls={`service-desc-${i}`}
                    >
                      <h3 className={styles.rowHead}>{head}</h3>
                      {price && <span className={styles.rowPrice}>{price}</span>}
                      {comingSoon && (
                        <span className={styles.rowSoon}>{i18n.t("Coming Soon")}</span>
                      )}
                    </button>

                    <div className={styles.rowBody} id={`service-desc-${i}`}>
                      <div className="color-nevada fs20 lh12 mt-4 lh15">{text}</div>
                      {btnText && (
                        <div className="flex items-center mt-4 gap-4 md:gap-8">
                          <button
                            type="button"
                            className={`fbce btn bold-f ${styles.btn}`}
                            style={{
                              "--color": btnColor,
                              "--btnBgColor": color,
                              "--btnHoverBg": Color(color).lighten(0.2),
                            }}
                            onClick={() => redirectToSpecialty(specialtyId)}
                            onFocus={() => setActiveIndex(i)}
                          >
                            {btnText}
                            <span className="sr-only">{head}</span>
                          </button>
                          {firstSlot && (
                            <div
                              className="flex gap-1"
                              style={{fontSize: 14, color: vcData[specialtyId].darker2}}
                            >
                              <span className="hidden lg:block">{i18n.t("Next")}:</span>
                              <span className="bold">{todayTomorrow2(i18n.lang, firstSlot)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
      </div>
      <div className="relative hidden lg:block">
        {slides.map((s, i) => {
          const isActive = i === activeIndex;
          const opacity = isActive ? "opacity-100" : "opacity-0";
          return (
            <Image
              key={s.head}
              src={`/static/img/v5/${s.img}.jpg`}
              fill
              className={`object-cover transition-opacity ${opacity}`}
              alt=""
            />
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(Services);
